import type { OrderKind, PrinterEntry, PrintingSettings } from "../../api/types";
import { t } from "../../i18n";
import {
  NOTHING,
  ORDER_KINDS,
  choiceOf,
  choose,
  leftOver,
  printsNothing,
  whatsFor,
  withReceipt,
  type Choice,
  type What,
} from "./output";

interface Props {
  printers: readonly PrinterEntry[];
  settings: PrintingSettings;
  onSave: (next: PrintingSettings, message: string) => void;
}

const COUNTS = [1, 2, 3];

/**
 * 2 · Wat drukt elke printer af? (Furkan, 11/10/2026): a row per printer, a column per kind of order. On a
 * narrow screen every printer is a block with the three kinds under each other: nothing slides sideways.
 */
export function OutputTable({ printers, settings, onSave }: Props) {
  const text = t.printing;
  const ids = printers.map((printer) => printer.id);

  const label = (choice: Choice) =>
    withReceipt(choice.what)
      ? text.output.times(text.output.what[choice.what], choice.count, choice.onlyIfMakes)
      : text.output.what[choice.what];

  const save = (printer: PrinterEntry, kind: OrderKind, choice: Choice) => {
    onSave(
      choose(settings, ids, printer.id, kind, choice),
      text.output.saved(printer.name, text.orderKinds[kind], label(choice)),
    );
  };

  return (
    <div className="pmatrix">
      <div className="pm-head" aria-hidden="true">
        <span>{text.columns.printer}</span>
        {ORDER_KINDS.map((kind) => (
          <span key={kind}>{text.orderKinds[kind]}</span>
        ))}
      </div>
      {printers.map((printer) => {
        const cells = settings.output[printer.id];
        const silent = ORDER_KINDS.filter((kind) => printsNothing(settings, printer.id, kind));
        return (
          <div key={printer.id} className="pm-row">
            <div className="pm-name">
              <b>{printer.name}</b> <span className="note">{printer.paper} mm</span>
              {silent.length > 0 && (
                <div className="pm-warn">
                  {text.output.printsNothing(silent.map((kind) => text.orderKinds[kind]).join(", "))}
                </div>
              )}
            </div>
            {ORDER_KINDS.map((kind) => {
              const cell = cells?.[kind] ?? NOTHING;
              const choice = choiceOf(cell);
              const left = leftOver(cell);
              const name = text.orderKinds[kind];
              return (
                <div key={kind} className="pm-cell" data-label={name}>
                  {/* what it prints, and how many times right next to it (Furkan, 18/10/2026: there is room) */}
                  <div className="pm-pick">
                    <select
                      aria-label={text.output.whatOf(printer.name, name)}
                      value={choice.what}
                      onChange={(event) => {
                        const what = event.target.value as What;
                        save(printer, kind, { what, count: choice.count, onlyIfMakes: false });
                      }}
                    >
                      {whatsFor(kind).map((what) => (
                        <option key={what} value={what}>
                          {text.output.what[what]}
                        </option>
                      ))}
                    </select>
                    {withReceipt(choice.what) && (
                      <select
                        className="pm-count"
                        aria-label={text.output.countOf(printer.name, name)}
                        value={choice.count}
                        onChange={(event) => {
                          save(printer, kind, { ...choice, count: Number(event.target.value) });
                        }}
                      >
                        {COUNTS.map((count) => (
                          <option key={count} value={count}>
                            {count}×
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {kind !== "table" && choice.what === "receipt" && (
                    <div className="pm-more">
                      <label className="pm-if">
                        <input
                          type="checkbox"
                          checked={choice.onlyIfMakes}
                          onChange={(event) => {
                            save(printer, kind, { ...choice, onlyIfMakes: event.target.checked });
                          }}
                        />
                        {text.output.onlyIfMakes}
                      </label>
                    </div>
                  )}
                  {left.copy > 0 && <div className="note">{text.output.copyLeft(left.copy)}</div>}
                  {left.ifMakes > 0 && <div className="note">{text.output.ifMakesLeft(left.ifMakes)}</div>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
